// TODO: Use the Localized MUI date picker
import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
/* eslint-disable jsx-a11y/label-has-associated-control */
import { di } from '../../../state/inversify.config';
import { CreateIncidentDomainService } from '../createIncidentDomainService';
import { isValidDateTime, parseDateTime } from '../createIncident.helper';

import { TextInput } from '../../../components/textInput/textInput';
import { SelectInput } from '../../../components/select/selectInput';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import {
  MechanismOfInjury,
  PlayType,
  SessionType,
} from './completeIncident.enum';
// eslint-disable-next-line import/no-cycle
import { CompleteIncidentOther } from './completeIncidentOther.smart';
import { useCustomTranslation } from '../../../translation/customTranslation';
import { currentLocaleSports } from '../../../locale/LocaleConfig';
import {
  createLocalisation,
  LocalisationProps,
} from '../../../translation/helpers/enumLocalisation';
import { dateTime, formatYYYYMMDD } from '../../../time/datetime';

export function CompleteIncidentSmart() {
  const { t } = useCustomTranslation();
  const otherVal: string = 'OTHER';
  const { addUserNotification } = useContext(UserNotificationContext);
  const [inputs, setInputs] = useState<{ [key: string]: string }>({
    incidentStartDate: '',
    incidentStartTime: '',
  });
  const [validDate, setValidDate] = useState(true);

  const [options, setOptions] = useState<{ [key: string]: string }>({
    activity: '',
    activityOther: '',
    sessionType: '',
    sessionTypeOther: '',
    playType: '',
    playTypeOther: '',
    headImpactType: '',
    headImpactTypeOther: '',
  });

  const incidentDomainService = di.get<CreateIncidentDomainService>(
    CreateIncidentDomainService,
  );
  const incidentCreationViewSelector =
    incidentDomainService.useIncidentCreateViewSelector();

  const canCreateNewIncident =
    incidentDomainService.useIncidentDateTimeValidationSelector();

  const activityOption = options.activity;
  const sessionTypeOption = options.sessionType;
  const playTypeOption = options.playType;
  const headImpactTypeOption = options.headImpactType;

  const sessionTypeList: LocalisationProps[] = Object.values(
    createLocalisation(SessionType, 'incident.sessionType'),
  );
  const playTypeList: LocalisationProps[] = Object.values(
    createLocalisation(PlayType, 'incident.playType'),
  );
  const headImpactTypeList: LocalisationProps[] = Object.values(
    createLocalisation(MechanismOfInjury, 'incident.mechanismOfInjury'),
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const onOptionsChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setOptions((values) => ({
      ...values,
      [name]: value,
    }));
  };

  const handleSave = () => {
    if (inputs.incidentStartDate && inputs.incidentStartTime) {
      const isDateValid = isValidDateTime(
        `${inputs.incidentStartDate} ${inputs.incidentStartTime}`,
      );
      setValidDate(isDateValid);

      // debugger;

      incidentDomainService.dispatchIncidentCreation({
        ...incidentCreationViewSelector,
        incidentDateTime: `${
          isDateValid &&
          parseDateTime(inputs.incidentStartDate, inputs.incidentStartTime)
        }`,
        activity:
          activityOption === otherVal ? options.activity : activityOption,
        activityOther: activityOption === otherVal ? options.activityOther : '',
        sessionType:
          sessionTypeOption === otherVal
            ? options.sessionType
            : sessionTypeOption,
        sessionTypeOther:
          sessionTypeOption === otherVal ? options.sessionTypeOther : '',
        playType:
          playTypeOption === otherVal ? options.playType : playTypeOption,
        playTypeOther: playTypeOption === otherVal ? options.playTypeOther : '',
        headImpactType:
          headImpactTypeOption === otherVal
            ? options.headImpactType
            : headImpactTypeOption,
        headImpactTypeOther:
          headImpactTypeOption === otherVal ? options.headImpactTypeOther : '',
      });
    }
  };

  useEffect(() => {
    setInputs((prevValues) => ({
      ...prevValues,
      incidentStartDate: dateTime.format(new Date(), formatYYYYMMDD),
      incidentStartTime: dateTime.format(new Date(), 'HH:mm:00'),
    }));
    setOptions(() => ({
      activity: incidentCreationViewSelector.activity as string,
      activityOther: incidentCreationViewSelector.activityOther as string,
      sessionType: incidentCreationViewSelector.sessionType as string,
      sessionTypeOther: incidentCreationViewSelector.sessionTypeOther as string,
      playType: incidentCreationViewSelector.playType as string,
      playTypeOther: incidentCreationViewSelector.playTypeOther as string,
      headImpactType: incidentCreationViewSelector.headImpactType as string,
      headImpactTypeOther:
        incidentCreationViewSelector.headImpactTypeOther as string,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSave();
    if (!canCreateNewIncident) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('incident.INVALID_INCIDENT_DATE'),
        UserNotificationType.ERROR,
      );
    }
    if (!validDate) {
      addUserNotification(
        UserNotificationType.ERROR,
        getInValidDateError(),
        UserNotificationType.ERROR,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs.incidentStartDate, inputs.incidentStartTime, options]);

  const getInValidDateError = () => {
    return process.env.NODE_ENV === 'development'
      ? `${t('INVALID_DATE')}. (Selected date/time is: "${
          inputs.incidentStartDate
        } ${inputs.incidentStartTime}")`
      : t('INVALID_DATE');
  };

  return (
    <Grid item xs={12} sm={8}>
      <Box display="flex" flexDirection="column">
        <TextInput
          name="incidentStartDate"
          type="date"
          required
          value={inputs.incidentStartDate || ''}
          label={t('incident.COMPLETE_INCIDENT_INCIDENT_START_LABEL')}
          onChange={handleInputChange}
          inputLabelProps={{ shrink: true }}
        />
        <TextInput
          name="incidentStartTime"
          type="time"
          required
          value={inputs.incidentStartTime || ''}
          label={t('incident.COMPLETE_INCIDENT_INCIDENT_END_LABEL')}
          onChange={handleInputChange}
          inputLabelProps={{ shrink: true }}
        />
        <SelectInput
          label={t('incident.sport.ACTIVITY_TITLE')}
          name="activity"
          items={currentLocaleSports.map((list) => {
            return {
              ...list,
              value: list.value,
              text: t(list.i18nKey),
            };
          })}
          onChange={onOptionsChange}
          sx={{ my: 1 }}
        />
        {activityOption === otherVal && (
          <CompleteIncidentOther
            name="activityOther"
            val={options.activityOther}
            setData={setOptions}
          />
        )}

        <SelectInput
          label={t('incident.sessionType.SESSION_TYPE_TITLE')}
          name="sessionType"
          items={sessionTypeList.map((list) => {
            return {
              ...list,
              text: t(list.i18nKey),
            };
          })}
          onChange={onOptionsChange}
          sx={{ my: 1 }}
        />
        {sessionTypeOption === otherVal && (
          <CompleteIncidentOther
            name="sessionTypeOther"
            val={options.sessionTypeOther}
            setData={setOptions}
          />
        )}

        <SelectInput
          label={t('incident.playType.PLAY_TYPE_TITLE')}
          name="playType"
          items={playTypeList.map((list) => {
            return {
              ...list,
              text: t(list.i18nKey),
            };
          })}
          onChange={onOptionsChange}
          sx={{ my: 1 }}
        />
        {playTypeOption === otherVal && (
          <CompleteIncidentOther
            name="playTypeOther"
            val={options.playTypeOther}
            setData={setOptions}
          />
        )}

        <SelectInput
          label={t('incident.mechanismOfInjury.HEAD_IMPACT_TYPE_TITLE')}
          name="headImpactType"
          items={headImpactTypeList.map((list) => {
            return {
              ...list,
              text: t(list.i18nKey),
            };
          })}
          onChange={onOptionsChange}
          sx={{ my: 1 }}
        />
        {headImpactTypeOption === otherVal && (
          <CompleteIncidentOther
            name="headImpactTypeOther"
            val={options.headImpactTypeOther}
            setData={setOptions}
          />
        )}
      </Box>
    </Grid>
  );
}
